import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, Download } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, Line, Tooltip, XAxis, YAxis, ComposedChart } from "recharts";
import { fetchMarketingAnalytics, exportMarketingAnalytics } from "../../api/AnalyticsAPIs";
import { formatMoney, formatPercent } from "../../utils/analyticsDates";
import { AttributionBadge } from "./attributionLabels";
import { ChartBox, EmptyState, SectionCard } from "./AnalyticsUi";
import TrackingLinkBuilder from "./TrackingLinkBuilder";

const rate = (value) => (value == null ? "—" : formatPercent(value));

function BehaviorCard({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-2 py-2.5 min-w-0 md:w-44 md:shrink-0 md:px-3 md:py-3">
      <p className="text-[11px] text-slate-500 leading-tight">{label}</p>
      <p className="mt-1 text-lg md:text-2xl font-bold text-slate-900">{Number(value || 0).toLocaleString("ar-EG")}</p>
      <p className="mt-1 text-[10px] text-slate-400 leading-snug">{hint}</p>
    </div>
  );
}

function BehaviorToggle({ open, onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={open}
      className={`inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 ${className}`}
    >
      <ChevronDown size={18} className={`md:hidden transition-transform ${open ? "rotate-180" : ""}`} />
      <ChevronLeft size={18} className={`hidden md:block transition-transform ${open ? "rotate-180" : ""}`} />
    </button>
  );
}

export function CustomerBehaviorCards({ filters }) {
  const [open, setOpen] = useState(false);
  const query = useQuery({
    queryKey: ["marketing-analytics", filters],
    queryFn: () => fetchMarketingAnalytics(filters),
  });
  const activity = query.data?.activity || {};
  const orders = query.data?.orders || {};
  const loading = query.isLoading;

  const primary = [
    ["زوار الموقع", activity.visitors, "زائر مميز في الفترة"],
    ["Add to cart", activity.cartAdds, `${activity.cartAddSessions || 0} جلسة حطت في السلة`],
    ["دخل الـ Checkout", activity.checkoutSessions, `${activity.checkoutWithContact || 0} كتب بياناته`],
  ];
  const extra = [
    ["جلسات", activity.sessions, "جلسة تصفح"],
    ["مشاهدات الصفحات", activity.pageViews, "عدد فتح الصفحات"],
    ["Add to wishlist", activity.wishlistAdds, `${activity.wishlistSessions || 0} جلسة`],
    ["دخل السلة", activity.cartViewSessions, "جلسة فتحت السلة"],
    ["طلبات اتعملت", orders.total, "عشان تقارن السلوك بالطلبات"],
  ];

  return (
    <section className="space-y-2">
      <div>
        <h2 className="text-sm md:text-base font-bold text-slate-800">سلوك العملاء</h2>
        <p className="text-[11px] text-slate-500">بتاريخ إنشاء الجلسة. فلتر الشحن وحالة الطلب مش بيحذف الزوار اللي ماعملوش أوردر.</p>
      </div>
      {loading ? (
        <div className="grid grid-cols-3 gap-2 md:flex md:gap-3">
          {primary.map(([label]) => <div key={label} className="h-24 rounded-2xl bg-white border border-slate-100 animate-pulse md:w-44" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2 md:hidden">
            {primary.map(([label, value, hint]) => <BehaviorCard key={label} label={label} value={value} hint={hint} />)}
            {open && extra.map(([label, value, hint]) => <BehaviorCard key={label} label={label} value={value} hint={hint} />)}
            <BehaviorToggle open={open} onClick={() => setOpen((value) => !value)} className="col-span-3 h-11" />
          </div>
          <div className="hidden md:flex md:items-stretch md:gap-3">
            <div className="flex min-w-0 flex-1 items-stretch gap-3 overflow-x-auto">
              {primary.map(([label, value, hint]) => <BehaviorCard key={label} label={label} value={value} hint={hint} />)}
              {open && extra.map(([label, value, hint]) => <BehaviorCard key={label} label={label} value={value} hint={hint} />)}
            </div>
            <BehaviorToggle open={open} onClick={() => setOpen((value) => !value)} className="w-12 shrink-0" />
          </div>
        </>
      )}
    </section>
  );
}

export default function MarketingOverview({ filters }) {
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const query = useQuery({
    queryKey: ["marketing-analytics", filters],
    queryFn: () => fetchMarketingAnalytics(filters),
  });
  const data = query.data;

  const exportCsv = async () => {
    setExporting(true);
    setExportError("");
    try {
      await exportMarketingAnalytics(filters);
    } catch (error) {
      setExportError(error.message || "تعذر التصدير");
    } finally {
      setExporting(false);
    }
  };

  const openChannel = (channel) => {
    const params = new URLSearchParams();
    if (channel) params.set("channel", channel);
    navigate(`/customer-analytics?${params.toString()}`);
  };

  if (query.isLoading) {
    return <div className="h-40 rounded-2xl bg-white border border-slate-100 animate-pulse" />;
  }
  if (query.error) {
    return <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">{query.error.message}</div>;
  }
  if (!data) return null;

  const activity = data.activity || {};
  const orders = data.orders || {};
  const shipping = data.shipping || {};

  return (
    <div className="space-y-4 md:space-y-6">
      <CollapsePanel
        title="التسويق والحملات"
        subtitle="القنوات، الحملات، ميتا وتيك توك"
        action={
          <button type="button" onClick={exportCsv} disabled={exporting} className="inline-flex items-center gap-1 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700">
            <Download size={14} />
            {exporting ? "جاري التصدير" : "CSV"}
          </button>
        }
      >
      <SectionCard
        title="النشاط والطلبات"
        subtitle="الزيارات بتاريخ إنشاء الجلسة. الطلبات بتاريخ إنشاء الطلب."
      >
        {exportError && <p className="mb-3 text-sm text-rose-700">{exportError}</p>}
        {data.orderOnlyFilters?.length > 0 && (
          <p className="mb-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
            فلاتر الطلب والشحن ({data.orderOnlyFilters.join("، ")}) بتطبق على الطلبات والشحن فقط، ومش بتحذف الزوار اللي لسه ماعملوش طلب.
          </p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-2">
          <Mini label="جلسات" value={activity.sessions} hint={`${activity.visitors || 0} زائر`} />
          <Mini label="مشاهدات الصفحات" value={activity.pageViews} />
          <Mini label="إضافة للسلة" value={activity.cartAdds} hint={`${activity.cartAddSessions || 0} جلسة`} />
          <Mini label="إضافة للمفضلة" value={activity.wishlistAdds} hint={`${activity.wishlistSessions || 0} جلسة`} />
          <Mini label="جلسات الدفع" value={activity.checkoutSessions} />
          <Mini label="طلبات" value={orders.total} hint={`${orders.pieces || 0} قطعة`} />
          <Mini label="مدفوع أونلاين" value={orders.paidOnline} />
          <Mini label="عند الاستلام" value={orders.cod} />
          <Mini label="أونلاين غير مدفوع" value={orders.unpaidOnline} />
          <Mini label="قيمة الطلبات" value={`${formatMoney(orders.revenue)} ج.م`} />
          <Mini label="متوسط الطلب" value={orders.averageOrderValue == null ? "—" : `${formatMoney(orders.averageOrderValue)} ج.م`} />
        </div>
        <p className="mt-3 text-[11px] leading-5 text-slate-500">{data.coverageNote} تغطية المصدر على الطلبات: {rate(orders.attributionCoverage)} ({orders.attributed || 0} معروف / {orders.unattributed || 0} غير معروف).</p>
      </SectionCard>

      <SectionCard title="مسار العميل" subtitle={data.activityDateBasis}>
        <div className="space-y-2">
          {(data.funnel || []).map((step) => (
            <div key={step.key} className="grid grid-cols-1 md:grid-cols-[180px_1fr_auto] gap-2 items-center text-sm">
              <div>
                <p className="font-medium text-slate-800">{step.label}</p>
                {step.note && <p className="text-[11px] text-slate-500">{step.note}</p>}
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-indigo-500" style={{ width: `${Math.min(100, step.fromFirst || (step.key === "sessions" ? 100 : 0))}%` }} />
              </div>
              <div className="text-left text-xs text-slate-600 whitespace-nowrap">
                {step.uniqueSessions?.toLocaleString("ar-EG")} جلسة
                {step.events !== step.uniqueSessions ? ` · ${step.events?.toLocaleString("ar-EG")} حدث` : ""}
                {step.fromPrevious != null ? ` · ${formatPercent(step.fromPrevious)} من السابق` : ""}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-500">التسليم بيحصل بعد الطلب، فمش متعامل كخطوة تحويل من نفس الجلسة. شوف قسم الشحن لتاريخ التسليم.</p>
      </SectionCard>

      <SectionCard title="أداء القنوات" subtitle="جلسة القناة من مصدر الزيارة. طلب القناة من لقطة آخر مصدر غير مباشر وقت إنشاء الطلب.">
        <div className="overflow-x-auto">
          <table className="min-w-[860px] w-full text-xs md:text-sm">
            <thead className="text-slate-500">
              <tr>
                {["القناة", "جلسات", "مشاهدات", "سلة", "دفع", "طلبات", "مدفوع", "تم التسليم", "القيمة", "متوسط الطلب", "قطع", "تسليم"].map((head) => (
                  <th key={head} className="px-2 py-2 text-right font-medium">{head}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.channels || []).map((row) => (
                <tr key={row.channel} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => openChannel(row.channel)}>
                  <td className="px-2 py-2"><AttributionBadge channel={row.channel} label={row.label} /></td>
                  <td className="px-2 py-2">{row.sessions}</td>
                  <td className="px-2 py-2">{row.pageViews}</td>
                  <td className="px-2 py-2">{row.cartAddSessions}</td>
                  <td className="px-2 py-2">{row.checkoutSessions}</td>
                  <td className="px-2 py-2">{row.orders}</td>
                  <td className="px-2 py-2">{row.paidOnline}</td>
                  <td className="px-2 py-2">{row.delivered}</td>
                  <td className="px-2 py-2">{formatMoney(row.revenue)}</td>
                  <td className="px-2 py-2">{row.averageOrderValue == null ? "—" : formatMoney(row.averageOrderValue)}</td>
                  <td className="px-2 py-2">{row.pieces}</td>
                  <td className="px-2 py-2">{rate(row.deliveryRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <SectionCard title="الحملات" subtitle="الحملات بتتجمع بالمصدر ومعرّف الحملة، مش بالاسم بس.">
          {(data.campaigns || []).length === 0 ? <EmptyState /> : (
            <div className="overflow-x-auto max-h-80">
              <table className="min-w-full text-xs">
                <thead className="text-slate-500">
                  <tr>{["القناة", "الحملة", "الوسيط", "جلسات", "طلبات", "القيمة", "تسليم"].map((head) => <th key={head} className="px-2 py-2 text-right">{head}</th>)}</tr>
                </thead>
                <tbody>
                  {data.campaigns.map((row) => (
                    <tr key={`${row.channel}-${row.campaignId || ""}-${row.campaign}`} className="border-t border-slate-100">
                      <td className="px-2 py-2"><AttributionBadge channel={row.channel} label={row.channelLabel} /></td>
                      <td className="px-2 py-2">{row.campaign}{row.campaignId ? <span className="block text-[10px] text-slate-400">{row.campaignId}</span> : null}</td>
                      <td className="px-2 py-2">{row.medium || "—"}</td>
                      <td className="px-2 py-2">{row.sessions}</td>
                      <td className="px-2 py-2">{row.orders}</td>
                      <td className="px-2 py-2">{formatMoney(row.revenue)}</td>
                      <td className="px-2 py-2">{row.delivered}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
        <SectionCard title="الاتجاه" subtitle="الطلبات والقيمة بتاريخ إنشاء الطلب. التسليم والمرتجع في الشارت لأحداث طلبات نفس الفترة فقط.">
          {(data.trend || []).length === 0 ? <EmptyState /> : (
            <ChartBox className="h-64 md:h-72">
              <ComposedChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="count" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="money" orientation="left" hide />
                <Tooltip />
                <Legend />
                <Bar yAxisId="count" dataKey="sessions" name="جلسات" fill="#6366f1" />
                <Bar yAxisId="count" dataKey="orders" name="طلبات" fill="#10b981" />
                <Line yAxisId="count" dataKey="cartAdds" name="إضافات السلة" stroke="#f59e0b" dot={false} />
              </ComposedChart>
            </ChartBox>
          )}
        </SectionCard>
      </div>

      <SectionCard title="إنفاق الإعلانات" subtitle={data.adSpendNote}>
        {data.metaSpend?.available ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <Mini label="إنفاق Meta" value={`${formatMoney(data.metaSpend.spend)} ج.م`} />
            <Mini label="مشتريات المنصة" value={data.metaSpend.platformPurchases} />
            <Mini label="قيمة مشتريات المنصة" value={`${formatMoney(data.metaSpend.platformPurchaseValue)} ج.م`} />
          </div>
        ) : (
          <p className="text-sm text-slate-600">{data.metaSpend?.note || data.adSpendNote}</p>
        )}
        <p className="mt-2 text-[11px] text-slate-500">مفيش ROAS محسوب لأن مشتريات المنصة مش هي عدد طلبات جيرابات.</p>
      </SectionCard>

      <SectionCard title="روابط التتبع" subtitle="القيم الافتراضية اقتراح. تقدر تغير المصدر والوسيط قبل النسخ.">
        <TrackingLinkBuilder />
      </SectionCard>
      </CollapsePanel>

      <CollapsePanel title="الشحن" subtitle="حالة الشحن لطلبات الفترة">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Mini label="تم التسليم" value={shipping.delivered} hint={rate(shipping.deliveryRate)} />
          <Mini label="مرتجع" value={shipping.returned} hint={rate(shipping.returnRate)} />
          <Mini label="في الطريق" value={(shipping.inTransit || 0) + (shipping.outForDelivery || 0)} />
          <Mini label="محاولة فاشلة" value={shipping.failedAttempt} hint={rate(shipping.failedAttemptRate)} />
          <Mini label="خارج للتوصيل" value={shipping.outForDelivery || 0} />
          <Mini label="مرتجع في الطريق" value={shipping.returnInProgress || 0} />
          <Mini label="شحن ملغي" value={shipping.cancelled || 0} />
          <Mini label="غير متزامن" value={shipping.unsynced || 0} />
        </div>
        <SectionCard title="الشحن حسب المصدر" subtitle={data.shippingDateBasis}>
          <p className="mb-2 text-[11px] text-slate-500">{shipping.deliveryRateDefinition} متوسط التسليم {shipping.averageDeliveryDays ?? "—"} يوم، الوسيط {shipping.medianDeliveryDays ?? "—"} يوم، من {shipping.deliveryTimeSample || 0} شحنة فيها طوابع زمنية. اتسلم خلال الفترة (بتاريخ التسليم): {data.deliveredDuringPeriod || 0}. رجع خلال الفترة: {data.returnedDuringPeriod || 0}. {data.deliveryEventNote}</p>
          {(data.shippingByChannel || []).length === 0 ? <EmptyState /> : (
            <table className="min-w-full text-sm">
              <thead className="text-slate-500 text-xs"><tr>{["القناة", "طلبات", "تم التسليم", "مرتجع", "في الطريق", "نسبة التسليم"].map((head) => <th key={head} className="px-2 py-1 text-right">{head}</th>)}</tr></thead>
              <tbody>
                {data.shippingByChannel.map((row) => (
                  <tr key={row.name} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">{row.name}</td>
                    <td className="px-2 py-1.5">{row.orders}</td>
                    <td className="px-2 py-1.5">{row.delivered}</td>
                    <td className="px-2 py-1.5">{row.returned}</td>
                    <td className="px-2 py-1.5">{row.inTransit}</td>
                    <td className="px-2 py-1.5">{rate(row.deliveryRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      </CollapsePanel>

      <SectionCard title="حالات الطلب وطريقة الدفع" subtitle={data.orderDateBasis}>
        <div className="grid grid-cols-3 gap-2 mb-3 text-sm">
          <Mini label="COD" value={data.payments?.cod} />
          <Mini label="أونلاين مدفوع" value={data.payments?.paidOnline} />
          <Mini label="أونلاين غير مدفوع" value={data.payments?.unpaidOnline} />
        </div>
        {(data.orderStatuses || []).length === 0 ? <EmptyState /> : (
          <ChartBox className="h-56 md:h-64">
            <BarChart data={data.orderStatuses.slice(0, 8)} layout="vertical" margin={{ left: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="label" width={90} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" name="طلبات" fill="#4f46e5" />
            </BarChart>
          </ChartBox>
        )}
        <p className="mt-2 text-[11px] text-slate-500">عملاء جدد: {orders.newCustomers || 0}. عملاء راجعين: {orders.returningCustomers || 0}. {orders.customerDefinition}</p>
      </SectionCard>
    </div>
  );
}

function CollapsePanel({ title, subtitle, action, children }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm min-w-0 overflow-hidden">
      <div className="flex items-center gap-3 p-4 md:p-5">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center justify-between gap-3 text-right"
        >
          <div className="min-w-0">
            <h2 className="text-base md:text-xl font-bold text-slate-800">{title}</h2>
            {subtitle && <p className="mt-1 text-[11px] md:text-xs leading-5 text-slate-500">{subtitle}</p>}
          </div>
          <ChevronDown size={20} className={`shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {action}
      </div>
      {open && <div className="space-y-4 px-4 pb-4 md:space-y-6 md:px-6 md:pb-6">{children}</div>}
    </section>
  );
}

function Mini({ label, value, hint }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 min-w-0">
      <p className="text-[11px] text-slate-500 truncate">{label}</p>
      <p className="text-sm md:text-base font-bold text-slate-900 truncate">{value ?? "—"}</p>
      {hint && <p className="text-[10px] text-slate-500">{hint}</p>}
    </div>
  );
}
